"use client"

import * as React from "react"
import { WeatherData, fetchWeatherData } from "@/lib/weather"

const WEATHER_STORAGE_KEY = "bold_full_weather_data"
const WEATHER_INFO_KEY = "bold_weather_info"
const WEATHER_COORDS_KEY = "bold_weather_coords"
const WEATHER_EVENT = "bold_weather_updated"

export function useWeather(lang: string = "pt") {
  const [weatherData, setWeatherData] = React.useState<WeatherData | null>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(WEATHER_STORAGE_KEY)
        if (saved) {
          return JSON.parse(saved) as WeatherData
        }
      } catch {}
    }
    return null
  })

  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [isLocationGranted, setIsLocationGranted] = React.useState<boolean>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem(WEATHER_STORAGE_KEY)
        if (saved) return true
      } catch {}
    }
    return false
  })

  const weatherInfo = React.useMemo(() => {
    if (weatherData) {
      return `${weatherData.cityName}, ${weatherData.currentTemp}°C`
    }
    return "Compartilhe sua localização"
  }, [weatherData])

  const requestLocation = React.useCallback(
    async (forcePrompt: boolean = false) => {
      if (typeof window === "undefined" || !("geolocation" in navigator)) {
        setError("Geolocalização não suportada neste dispositivo.")
        return
      }

      setIsLoading(true)
      setError(null)

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const lat = position.coords.latitude
            const lon = position.coords.longitude

            // Salva coordenadas para cache
            try {
              localStorage.setItem(WEATHER_COORDS_KEY, JSON.stringify({ lat, lon }))
            } catch {}

            const data = await fetchWeatherData(lat, lon, undefined, lang)
            setWeatherData(data)
            setIsLocationGranted(true)
            setIsLoading(false)

            try {
              localStorage.setItem(WEATHER_STORAGE_KEY, JSON.stringify(data))
              localStorage.setItem(WEATHER_INFO_KEY, `${data.cityName}, ${data.currentTemp}°C`)
            } catch {}

            // Notifica todos os widgets e componentes na janela
            window.dispatchEvent(
              new CustomEvent(WEATHER_EVENT, { detail: data })
            )
          } catch (err: any) {
            console.error("Erro ao buscar dados meteorológicos:", err)
            setError("Não foi possível carregar os dados de clima.")
            setIsLoading(false)
          }
        },
        (geoError) => {
          console.warn("Geolocalização rejeitada ou inacessível:", geoError)
          setIsLoading(false)
          setIsLocationGranted(false)
          if (forcePrompt) {
            setError(
              "Permissão de localização negada. Habilite o acesso nas configurações do seu navegador para ver o clima real."
            )
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 12000,
          maximumAge: 300000,
        }
      )
    },
    [lang]
  )

  // Sincroniza com eventos emitidos por outros componentes
  React.useEffect(() => {
    const handleWeatherUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<WeatherData>
      if (customEvent.detail) {
        setWeatherData(customEvent.detail)
        setIsLocationGranted(true)
      }
    }

    window.addEventListener(WEATHER_EVENT, handleWeatherUpdate)
    return () => window.removeEventListener(WEATHER_EVENT, handleWeatherUpdate)
  }, [])

  // Inicialização inteligente: checa permissão existente ou dados em cache
  React.useEffect(() => {
    if (typeof window === "undefined") return

    // Se já temos dados salvos, atualiza se o cache tiver mais de 30 minutos
    if (weatherData && Date.now() - (weatherData.lastUpdated || 0) < 30 * 60 * 1000) {
      return
    }

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            requestLocation(false)
          } else if (result.state === "prompt") {
            // Tenta usar as últimas coordenadas em cache caso existam
            try {
              const savedCoords = localStorage.getItem(WEATHER_COORDS_KEY)
              if (savedCoords) {
                const { lat, lon } = JSON.parse(savedCoords)
                fetchWeatherData(lat, lon, undefined, lang).then((data) => {
                  setWeatherData(data)
                  setIsLocationGranted(true)
                })
              }
            } catch {}
          }
        })
        .catch(() => {
          // Fallback silencioso
        })
    }
  }, [requestLocation, lang, weatherData])

  return {
    weatherData,
    weatherInfo,
    isLoading,
    error,
    isLocationGranted,
    requestLocation,
  }
}
